import * as React from 'react';
import styled from '@emotion/styled';
import { ReactComponent as TODO } from './icons/TO_DO.svg';
import { ReactComponent as DOING } from './icons/DOING.svg';
import { ReactComponent as NEW } from './icons/NEW.svg';
import { ReactComponent as QUANTITY } from './icons/QUANTITY.svg';
import { ReactComponent as DONE } from './icons/DONE.svg';
import { ReactComponent as TO_ASSEMBLE } from './icons/TO_ASSEMBLE.svg';
import { ReactComponent as DELIVERED } from './icons/DELIVERED.svg';
import { ReactComponent as AWAITING_DELIVERY } from './icons/AWAITING_DELIVERY.svg';
import { assertUnreachable } from '../../../../utils/typescript';
import { LineStatus } from '../../../../redux/tickets/types/kds-line/status';
import { getIsProductFollowUpEnabled } from '../../../../redux/configuration/selectors';
import { useSelector } from 'react-redux';

const iconSize = 24;

const Container = styled.span<{ width?: number; height?: number }>`
    display: inline-block;
    width: ${({ width }) => width ?? iconSize}px;
    height: ${({ height }) => height ?? iconSize}px;
`;

type Props = {
    status: LineStatus;
};

type MappedIcons = {
    Icon: React.FunctionComponent<React.PropsWithChildren<React.SVGProps<SVGSVGElement>>> | undefined;
    label: string;
    width?: number;
    height?: number;
};

const mapIconToStatus = (status: LineStatus, isProductFollowUpEnabled: boolean): MappedIcons => {
    switch (status) {
        case LineStatus.New:
            return { Icon: NEW, label: LineStatus.New };
        case LineStatus.ToDo:
            return { Icon: TODO, label: LineStatus.ToDo };
        case LineStatus.Cancelled:
            return { Icon: undefined, label: LineStatus.Cancelled };
        case LineStatus.Doing:
            return { Icon: DOING, label: LineStatus.Doing };
        case LineStatus.Done:
            if (isProductFollowUpEnabled) {
                return { Icon: AWAITING_DELIVERY, label: LineStatus.Done };
            }
            return { Icon: DONE, label: LineStatus.Done };
        case LineStatus.QuantityModified:
            return { Icon: QUANTITY, label: LineStatus.QuantityModified };
        case LineStatus.ToAssemble:
            return { Icon: TO_ASSEMBLE, label: LineStatus.ToAssemble };
        case LineStatus.Delivered:
            return { Icon: DELIVERED, label: LineStatus.Delivered, width: 50 };
        case LineStatus.Waiting:
            return { Icon: undefined, label: LineStatus.Waiting };
        default:
            return assertUnreachable(status);
    }
};

const ItemIcon: React.FunctionComponent<React.PropsWithChildren<Props>> = ({ status }) => {
    const isProductFollowUpEnabled = useSelector(getIsProductFollowUpEnabled);
    const { Icon, label, width, height } = mapIconToStatus(status, isProductFollowUpEnabled);
    const resolvedWidth = width ?? iconSize;
    const resolvedHeight = height ?? iconSize;

    return (
        <Container width={resolvedWidth} height={resolvedHeight}>
            {Icon && <Icon data-testid={label} width={resolvedWidth} height={resolvedHeight} />}
        </Container>
    );
};

export default ItemIcon;
